import { z } from "zod";
import { zodResolver } from "@hookform/resolvers/zod";
import { useForm } from "react-hook-form";
import { Form, FormControl, FormField, FormItem, FormMessage } from "@/components/ui/form";
import { Button } from "@/components/ui/button";
import React from "react";
import { LoadingSmall } from "@/components/LoadingSmall";
import { useTranslation } from "react-i18next";
import { TargetEmotion, TargetGender, TargetAge } from "@/lib/types";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Accordion, AccordionContent, AccordionItem, AccordionTrigger } from "@/components/ui/accordion";
import { Checkbox } from "@/components/ui/checkbox";
import { Label } from "@/components/ui/label";
import { Plus, Trash2, Heart, Users, User, MapPin } from "lucide-react";
import { useFieldArray } from "react-hook-form";
import { NestedAreaSelector } from "@/components/NestedAreaSelector";

const formSchema = z.object({
  // Conditional targeting fields
  // we need to allow user selecting multiple conditions and a contentGroupId, see `addCampaignCondition` for structure
  conditions: z
    .array(
      z.object({
        emotions: z.array(z.nativeEnum(TargetEmotion)).optional(),
        ageRanges: z.array(z.nativeEnum(TargetAge)).optional(),
        genders: z.array(z.nativeEnum(TargetGender)).optional(),
        areas: z.array(z.number()).optional(),
      })
    )
    .optional(),
});

export type FormSchemaType = z.infer<typeof formSchema>;

type Props = {
  buttonText: string;
  onSubmit: (values: FormSchemaType) => void;
  isLoading: boolean;
  defaultValues?: FormSchemaType;
  showTypeSelect: boolean;
};

export const CampaignConditionsMetaForm: React.FC<Props> = ({ buttonText, onSubmit, isLoading, defaultValues }) => {
  const { t } = useTranslation();

  const form = useForm<FormSchemaType>({
    resolver: zodResolver(formSchema),
    defaultValues: defaultValues || {
      conditions: [],
    },
  });

  const { fields, append, remove } = useFieldArray({
    control: form.control,
    name: "conditions",
  });

  const AgeTranslation: Record<TargetAge, string> = {
    [TargetAge.A0]: "N/A",
    [TargetAge.A018]: "0-18",
    [TargetAge.A1929]: "19-29",
    [TargetAge.A3039]: "30-39",
    [TargetAge.A4049]: "40-49",
    [TargetAge.A5059]: "50-59",
    [TargetAge.A60100]: "60+",
  };

  const EmotionTranslation: Record<TargetEmotion, string> = {
    [TargetEmotion.E0]: "N/A",
    [TargetEmotion.happy]: "happy",
    [TargetEmotion.neutral]: "neutral",
    [TargetEmotion.sad]: "sad",
    [TargetEmotion.surprize]: "surprize",
    [TargetEmotion.angry]: "angry",
  };

  const GenderTranslation: Record<TargetGender, string> = {
    [TargetGender.Male]: t("user.male"),
    [TargetGender.Female]: t("user.female"),
  };

  const addNewCondition = () => {
    append({
      emotions: [],
      ageRanges: [],
      genders: [],
      areas: [],
    });
  };

  return (
    <>
      <Form {...form}>
        <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-8">
          <div className="space-y-4">
            <div className="flex items-center justify-between">
              <h3 className="text-lg font-medium">&nbsp;</h3>
              <Button
                type="button"
                variant="outline"
                size="sm"
                onClick={addNewCondition}
                className="flex items-center gap-2"
              >
                <Plus className="w-4 h-4" />
                {t("Add Condition")}
              </Button>
            </div>

            {fields.length === 0 && (
              <Card>
                <CardContent className="p-6 text-center text-muted-foreground">
                  {t("No conditions added yet. Click 'Add Condition' to start.")}
                </CardContent>
              </Card>
            )}

            {fields.map((field, index) => {
              const currentCondition = form.watch(`conditions.${index}`);
              const selectedCount =
                (currentCondition?.emotions?.length || 0) +
                (currentCondition?.ageRanges?.length || 0) +
                (currentCondition?.genders?.length || 0) +
                (currentCondition?.areas?.length || 0);

              return (
                <Card key={field.id} className="border-l-4 border-l-primary/50">
                  <CardHeader className="flex flex-row items-center justify-between space-y-0 pb-4">
                    <CardTitle className="text-base">
                      {t("Condition")} {index + 1}
                      {selectedCount > 0 && (
                        <span className="ml-2 text-sm font-normal text-muted-foreground">
                          ({selectedCount} selected)
                        </span>
                      )}
                    </CardTitle>
                    <Button
                      type="button"
                      variant="ghost"
                      size="sm"
                      onClick={() => remove(index)}
                      className="text-destructive hover:text-destructive"
                    >
                      <Trash2 className="w-4 h-4" />
                    </Button>
                  </CardHeader>
                  <CardContent>
                    <div className="space-y-4">
                      {/* Targeting Fields */}
                      <Accordion type="multiple" defaultValue={[]}>
                        {/* Emotions */}
                        <AccordionItem value="emotions">
                          <AccordionTrigger className="flex items-center gap-2 justify-start">
                            <Heart className="w-4 h-4 text-red-500" />
                            {t("Emotions")}
                            {(currentCondition?.emotions?.length || 0) > 0 && (
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                ({currentCondition?.emotions?.length || 0})
                              </span>
                            )}
                          </AccordionTrigger>
                          <AccordionContent>
                            <FormField
                              control={form.control}
                              name={`conditions.${index}.emotions`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="grid grid-cols-3 gap-2">
                                      {Object.entries(TargetEmotion)
                                        .filter(([key]) => isNaN(Number(key)))
                                        .map(([key, value]) => (
                                          <div key={key} className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`emotion-${index}-${key}`}
                                              checked={field.value?.includes(value as TargetEmotion) || false}
                                              onCheckedChange={(checked) => {
                                                const currentValues = field.value || [];
                                                if (checked) {
                                                  field.onChange([...currentValues, value as TargetEmotion]);
                                                } else {
                                                  field.onChange(currentValues.filter((v) => v !== value));
                                                }
                                              }}
                                            />
                                            <Label htmlFor={`emotion-${index}-${key}`} className="text-sm">
                                              {EmotionTranslation[value as TargetEmotion]}
                                            </Label>
                                          </div>
                                        ))}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </AccordionContent>
                        </AccordionItem>

                        {/* Age Ranges */}
                        <AccordionItem value="ages">
                          <AccordionTrigger className="flex items-center gap-2 justify-start">
                            <Users className="w-4 h-4 text-blue-500" />
                            {t("Age Ranges")}
                            {(currentCondition?.ageRanges?.length || 0) > 0 && (
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                ({currentCondition?.ageRanges?.length || 0})
                              </span>
                            )}
                          </AccordionTrigger>
                          <AccordionContent>
                            <FormField
                              control={form.control}
                              name={`conditions.${index}.ageRanges`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="grid grid-cols-3 gap-2">
                                      {Object.entries(TargetAge)
                                        .filter(([key]) => isNaN(Number(key)))
                                        .map(([key, value]) => (
                                          <div key={key} className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`age-${index}-${key}`}
                                              checked={field.value?.includes(value as TargetAge) || false}
                                              onCheckedChange={(checked) => {
                                                const currentValues = field.value || [];
                                                if (checked) {
                                                  field.onChange([...currentValues, value as TargetAge]);
                                                } else {
                                                  field.onChange(currentValues.filter((v) => v !== value));
                                                }
                                              }}
                                            />
                                            <Label htmlFor={`age-${index}-${key}`} className="text-sm">
                                              {AgeTranslation[value as TargetAge]}
                                            </Label>
                                          </div>
                                        ))}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </AccordionContent>
                        </AccordionItem>

                        {/* Genders */}
                        <AccordionItem value="genders">
                          <AccordionTrigger className="flex items-center gap-2 justify-start">
                            <User className="w-4 h-4 text-green-500" />
                            {t("Genders")}
                            {(currentCondition?.genders?.length || 0) > 0 && (
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                ({currentCondition?.genders?.length || 0})
                              </span>
                            )}
                          </AccordionTrigger>
                          <AccordionContent>
                            <FormField
                              control={form.control}
                              name={`conditions.${index}.genders`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <div className="flex gap-4">
                                      {Object.entries(TargetGender)
                                        .filter(([key]) => isNaN(Number(key)))
                                        .map(([key, value]) => (
                                          <div key={key} className="flex items-center space-x-2">
                                            <Checkbox
                                              id={`gender-${index}-${key}`}
                                              checked={field.value?.includes(value as TargetGender) || false}
                                              onCheckedChange={(checked) => {
                                                const currentValues = field.value || [];
                                                if (checked) {
                                                  field.onChange([...currentValues, value as TargetGender]);
                                                } else {
                                                  field.onChange(currentValues.filter((v) => v !== value));
                                                }
                                              }}
                                            />
                                            <Label htmlFor={`gender-${index}-${key}`} className="text-sm">
                                              {GenderTranslation[value as TargetGender]}
                                            </Label>
                                          </div>
                                        ))}
                                    </div>
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </AccordionContent>
                        </AccordionItem>

                        {/* Areas */}
                        <AccordionItem value="areas">
                          <AccordionTrigger className="flex items-center gap-2 justify-start">
                            <MapPin className="w-4 h-4 text-yellow-500" />
                            {t("Areas")}
                            {(currentCondition?.areas?.length || 0) > 0 && (
                              <span className="ml-2 text-sm font-normal text-muted-foreground">
                                ({currentCondition?.areas?.length || 0})
                              </span>
                            )}
                          </AccordionTrigger>
                          <AccordionContent>
                            <FormField
                              control={form.control}
                              name={`conditions.${index}.areas`}
                              render={({ field }) => (
                                <FormItem>
                                  <FormControl>
                                    <NestedAreaSelector
                                      selectedAreaIds={field.value || []}
                                      onSelectionChange={field.onChange}
                                    />
                                  </FormControl>
                                  <FormMessage />
                                </FormItem>
                              )}
                            />
                          </AccordionContent>
                        </AccordionItem>
                      </Accordion>
                    </div>
                  </CardContent>
                </Card>
              );
            })}
          </div>
          <Button type="submit" disabled={isLoading}>
            {buttonText}
            {isLoading && <LoadingSmall />}
          </Button>
        </form>
      </Form>
    </>
  );
};
